import React from 'react'
import { ChevronDown, ChevronUp, Loader2, Star, Upload, X, Image as ImageIcon, Sparkles } from 'lucide-react'

const STYLE_TAGS = ['电影风格', '奇幻', '写实', '动漫', '赛博朋克', '油画感']
const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4']

interface PromptCraftSidebarProps {
  mode: 'generate' | 'decompose' | 'vision'
  setMode: (mode: 'generate' | 'decompose' | 'vision') => void
  seedPrompt: string
  setSeedPrompt: (v: string) => void
  stylePreference: string
  setStylePreference: (v: string) => void
  aspectRatio: string
  setAspectRatio: (v: string) => void
  showAdvanced: boolean
  setShowAdvanced: (v: boolean) => void
  decomposePrompt: string
  setDecomposePrompt: (v: string) => void
  visionPreviewUrl: string | null
  isDragging: boolean
  setIsDragging: (v: boolean) => void
  visionUnderstanding: boolean
  fileInputRef: React.RefObject<HTMLInputElement>
  isGenerating: boolean
  visionFile: File | null
  onGenerate: () => void
  onDecompose: () => void
  onVisionFileSelect: (file: File) => void
  onVisionAnalyze: () => void
  onClearVision: () => void
  onOpenPreview: (url: string) => void
}

export function PromptCraftSidebar({
  mode, setMode,
  seedPrompt, setSeedPrompt,
  stylePreference, setStylePreference,
  aspectRatio, setAspectRatio,
  showAdvanced, setShowAdvanced,
  decomposePrompt, setDecomposePrompt,
  visionPreviewUrl, isDragging, setIsDragging,
  visionUnderstanding, fileInputRef, isGenerating, visionFile,
  onGenerate, onDecompose, onVisionFileSelect, onVisionAnalyze, onClearVision, onOpenPreview,
}: PromptCraftSidebarProps) {
  return (
    <div className="w-72 flex-shrink-0 space-y-4 overflow-y-auto">
      {/* Mode Toggle */}
      <div className="bg-[#161b22] rounded-xl p-3 border border-cyan-900/30 flex gap-2">
        <button
          onClick={() => setMode('generate')}
          className={`flex-1 py-2 text-xs rounded-lg font-medium transition-colors ${
            mode === 'generate'
              ? 'bg-cyan-500 text-white'
              : 'bg-cyan-900/20 text-cyan-400 border border-cyan-900/50 hover:bg-cyan-900/40'
          }`}
        >
          生成
        </button>
        <button
          onClick={() => setMode('decompose')}
          className={`flex-1 py-2 text-xs rounded-lg font-medium transition-colors ${
            mode === 'decompose'
              ? 'bg-purple-500 text-white'
              : 'bg-purple-900/20 text-purple-400 border border-purple-900/50 hover:bg-purple-900/40'
          }`}
        >
          拆解
        </button>
        <button
          onClick={() => setMode('vision')}
          className={`flex-1 py-2 text-xs rounded-lg font-medium transition-colors ${
            mode === 'vision'
              ? 'bg-amber-500 text-white'
              : 'bg-amber-900/20 text-amber-400 border border-amber-900/50 hover:bg-amber-900/40'
          }`}
        >
          图片理解
        </button>
      </div>

      {mode === 'generate' ? (
        <>
          <div className="bg-[#161b22] rounded-xl p-4 border border-cyan-900/30">
            <label className="block text-sm font-medium text-gray-300 mb-2">🌱 创意种子</label>
            <textarea
              value={seedPrompt}
              onChange={(e) => setSeedPrompt(e.target.value)}
              placeholder="一位银发精灵少女在雾气森林中..."
              className="w-full h-32 bg-[#0d1117] border border-cyan-900/30 rounded-lg px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 resize-none"
              maxLength={500}
            />
            <div className="text-xs text-gray-600 text-right mt-1">{seedPrompt.length}/500</div>
          </div>

          <div className="bg-[#161b22] rounded-xl p-4 border border-cyan-900/30">
            <label className="block text-sm font-medium text-gray-300 mb-2">🎨 风格偏好 (选填)</label>
            <input
              type="text"
              value={stylePreference}
              onChange={(e) => setStylePreference(e.target.value)}
              placeholder="运动和动感，电影级动态..."
              className="w-full bg-[#0d1117] border border-cyan-900/30 rounded-lg px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {STYLE_TAGS.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setStylePreference(tag)}
                  className={`px-2 py-1 text-xs rounded-full transition-colors ${
                    stylePreference === tag
                      ? 'bg-cyan-500 text-white'
                      : 'bg-cyan-900/20 text-cyan-400 border border-cyan-900/50 hover:bg-cyan-900/40'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 overflow-hidden">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full px-4 py-3 flex items-center justify-between text-gray-300 hover:bg-cyan-900/10 transition-colors"
            >
              <span className="text-sm font-medium">⚙️ 高级参数</span>
              {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showAdvanced && (
              <div className="px-4 pb-4 space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-2">比例</label>
                  <div className="flex flex-wrap gap-2">
                    {ASPECT_RATIOS.map((ratio) => (
                      <button
                        key={ratio}
                        onClick={() => setAspectRatio(ratio)}
                        className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                          aspectRatio === ratio
                            ? 'bg-cyan-500 text-white'
                            : 'bg-cyan-900/20 text-cyan-400 border border-cyan-900/50 hover:bg-cyan-900/40'
                        }`}
                      >
                        {ratio}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onGenerate}
            disabled={isGenerating || !seedPrompt.trim()}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-xl hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin" />生成中...</>
            ) : (
              <><Star className="w-5 h-5" />生成提示词</>
            )}
          </button>
        </>
      ) : mode === 'decompose' ? (
        <>
          <div className="bg-[#161b22] rounded-xl p-4 border border-purple-900/30">
            <label className="block text-sm font-medium text-gray-300 mb-2">📋 完整提示词</label>
            <textarea
              value={decomposePrompt}
              onChange={(e) => setDecomposePrompt(e.target.value)}
              placeholder="粘贴你的完整提示词，AI 将自动拆解为结构化字段..."
              className="w-full h-48 bg-[#0d1117] border border-purple-900/30 rounded-lg px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 resize-none"
              maxLength={2000}
            />
            <div className="text-xs text-gray-600 text-right mt-1">{decomposePrompt.length}/2000</div>
          </div>

          <button
            onClick={onDecompose}
            disabled={isGenerating || !decomposePrompt.trim()}
            className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin" />拆解中...</>
            ) : (
              <><Sparkles className="w-5 h-5" />拆解提示词</>
            )}
          </button>
        </>
      ) : (
        <>
          <div className="bg-[#161b22] rounded-xl p-4 border border-amber-900/30">
            <label className="block text-sm font-medium text-gray-300 mb-2">📷 上传图片</label>
            {!visionPreviewUrl ? (
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragging
                    ? 'border-amber-400 bg-amber-400/10'
                    : 'border-amber-900/30 hover:border-amber-500/50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
                onDrop={(e) => {
                  e.preventDefault(); setIsDragging(false)
                  const file = e.dataTransfer.files?.[0]
                  if (file) onVisionFileSelect(file)
                }}
              >
                <Upload className="w-10 h-10 mx-auto mb-3 text-gray-500" />
                <p className="text-sm text-gray-300 mb-1">拖拽图片到此处，或点击上传</p>
                <p className="text-xs text-gray-500 mb-3">支持 JPG, PNG, WebP，最大 10MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onVisionFileSelect(f) }}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm hover:from-amber-600 hover:to-orange-600 transition-colors"
                >
                  选择图片
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <img src={visionPreviewUrl} alt="Preview" className="w-full max-h-48 object-contain rounded-lg border border-amber-900/30 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => onOpenPreview(visionPreviewUrl!)} />
                  {!isGenerating && (
                    <button
                      onClick={onClearVision}
                      className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-lg text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {visionUnderstanding && (
                  <div className="flex items-center gap-2 text-amber-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>正在理解图片内容...</span>
                  </div>
                )}
                {isGenerating && !visionUnderstanding && (
                  <div className="flex items-center gap-2 text-cyan-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>正在拆解为思维导图...</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-amber-900/10 border border-amber-500/20 rounded-lg p-3">
            <p className="text-amber-400 text-xs">图片理解将消耗 1 个配额</p>
          </div>

          <button
            onClick={onVisionAnalyze}
            disabled={isGenerating || !visionFile}
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin" />{visionUnderstanding ? '理解中...' : '拆解中...'}</>
            ) : (
              <><ImageIcon className="w-5 h-5" />理解并拆解</>
            )}
          </button>
        </>
      )}
    </div>
  )
}
